import type { SubmitFeedback, FormField } from "shared"

export type Answers = Record<string, string | number | boolean>

/**
 * Groups the served fields into wizard steps. Every field starts its own step,
 * except a checkbox, which joins the immediately preceding step rather than
 * getting one of its own — this is how a checkbox ends up "on the same page"
 * as whatever question precedes it (e.g. community-education's two consent
 * checkboxes, seeded directly after the additional-comments textarea).
 */
export function buildSteps(fields: FormField[]): FormField[][] {
  const steps: FormField[][] = []
  for (const field of fields) {
    if (field.type === "checkbox" && steps.length > 0) {
      steps[steps.length - 1].push(field)
    } else {
      steps.push([field])
    }
  }
  return steps
}

export function buildSubmission(
  fields: FormField[],
  answers: Answers,
  invitationId: string,
): SubmitFeedback {
  const ratings = Object.fromEntries(
    fields
      .filter((f) => f.type === "rating" || f.type === "rating-pictogram")
      // Every served rating is reported. An unanswered one is sent as null —
      // "shown but skipped" — rather than coerced to 0 (which the 1..5 wire
      // schema rejects) or omitted (which can't be told from "never shown").
      .map((f) => {
        const answer = answers[f.id]
        return [
          f.id,
          answer === undefined || answer === "" ? null : Number(answer),
        ]
      }),
  ) as SubmitFeedback["ratings"]

  const textareaField = fields.find((f) => f.type === "textarea")
  const comment = textareaField ? answers[textareaField.id] : undefined

  // Every served checkbox is reported, unchecked reporting as `false` rather
  // than being omitted — an unticked box is a real "no", not "not shown"
  // (mirrors how a skipped rating is still reported, just as null).
  const checkboxFields = fields.filter((f) => f.type === "checkbox")
  const checkboxes = Object.fromEntries(
    checkboxFields.map((f) => [f.id, Boolean(answers[f.id])]),
  )

  return {
    invitationId,
    ratings,
    ...(comment ? { additionalComment: String(comment) } : {}),
    ...(checkboxFields.length > 0 ? { checkboxes } : {}),
  }
}
